import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, Flex, Icon, Loading, Page, SearchBar, Typo, theme } from '../../../ui';
import PageSection from '../../../ui/Page/PageSection';
import { useClassrooms } from '../hooks';
import { isDemoClassroom } from '../data/mockClassroomData';
import ClassroomWizard from './ClassroomWizard';

const CONTENT_STANDARD = '1200px';

const ClassroomGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  width: 100%;
`;

const ClassroomCard = styled.div`
  background: ${theme.colors.NEU_0};
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  border: 1px solid ${theme.colors.NEU_100};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border-color: ${theme.colors.PRIMARY};
  }
`;

const CardHeader = styled(Flex)`
  margin-bottom: 12px;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${theme.colors.PRIMARY_EXTRA_LIGHT};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MetaRow = styled(Flex)`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${theme.colors.NEU_100};
`;

const MetaBadge = styled(Flex)`
  background: ${theme.colors.NEU_50};
  border-radius: 6px;
  padding: 4px 10px;
`;

const DemoBadge = styled.span`
  background: #fff3e0;
  color: #e65100;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EmptyState = styled(Flex)`
  padding: 4rem 2rem;
  text-align: center;
`;

const ClassroomList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { classrooms, isLoading } = useClassrooms();
  const [searchQuery, setSearchQuery] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  const filteredClassrooms = searchQuery
    ? classrooms.filter((c) => c.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : classrooms;

  const handleCardClick = useCallback(
    (groupId) => {
      navigate(`/classroom/${groupId}`);
    },
    [navigate]
  );

  if (isLoading) {
    return (
      <Page>
        <PageSection>
          <Loading />
        </PageSection>
      </Page>
    );
  }

  return (
    <Page>
      <PageSection gap='30'>
      <Flex justify='space-between' align='center' width='100%'>
        <Flex direction='column' gap='4'>
          <Typo.PageHeader>{t('classroom.title', 'Classrooms')}</Typo.PageHeader>
          <Typo.MediumText color={theme.colors.NEU_600}>
            {t('classroom.subtitle', 'Manage your live online classrooms')}
          </Typo.MediumText>
        </Flex>
        <Button onClick={() => setWizardOpen(true)}>
          {t('classroom.create', 'Create Classroom')}
        </Button>
      </Flex>

      {classrooms.length > 3 && (
        <SearchBar
          placeholder={t('classroom.search', 'Search classrooms...')}
          onChange={(e) => setSearchQuery(e.target.value)}
          value={searchQuery}
        />
      )}

      {filteredClassrooms.length === 0 ? (
        <EmptyState direction='column' gap='16' align='center' justify='center'>
          <IconCircle>
            <Icon.Student color={theme.colors.PRIMARY} size='large' />
          </IconCircle>
          <Typo.ContentSmallHeader>
            {t('classroom.empty', 'No classrooms yet')}
          </Typo.ContentSmallHeader>
          <Typo.MediumText color={theme.colors.NEU_600}>
            {t('classroom.emptyDescription', 'Create your first classroom to get started with live online teaching')}
          </Typo.MediumText>
          <Button onClick={() => setWizardOpen(true)}>
            {t('classroom.createFirst', 'Create Your First Classroom')}
          </Button>
        </EmptyState>
      ) : (
        <ClassroomGrid>
          {filteredClassrooms.map((classroom) => {
            const isDemo = isDemoClassroom(classroom.id);
            return (
              <ClassroomCard key={classroom.id} onClick={() => handleCardClick(classroom.id)}>
                <CardHeader align='center' gap='12'>
                  <IconCircle>
                    <Icon.Student color={theme.colors.PRIMARY} />
                  </IconCircle>
                  <Flex direction='column' gap='2' style={{ flex: 1 }}>
                    <Flex align='center' gap='8'>
                      <Typo.ContentSmallHeader>{classroom.name}</Typo.ContentSmallHeader>
                      {isDemo && <DemoBadge>Demo</DemoBadge>}
                    </Flex>
                    {classroom.description && (
                      <Typo.SmallText color={theme.colors.NEU_300}>
                        {classroom.description}
                      </Typo.SmallText>
                    )}
                  </Flex>
                </CardHeader>
                <MetaRow gap='10' align='center'>
                  <MetaBadge gap='4' align='center'>
                    <Icon.Users color={theme.colors.NEU_600} size='x-small' />
                    <Typo.SmallText color={theme.colors.NEU_600}>
                      {classroom.studentCount ?? 0} {t('classroom.students', 'students')}
                    </Typo.SmallText>
                  </MetaBadge>
                  <MetaBadge gap='4' align='center'>
                    <Icon.Course color={theme.colors.NEU_600} size='x-small' />
                    <Typo.SmallText color={theme.colors.NEU_600}>
                      {classroom.courseCount ?? 0} {t('classroom.units', 'units')}
                    </Typo.SmallText>
                  </MetaBadge>
                </MetaRow>
              </ClassroomCard>
            );
          })}
        </ClassroomGrid>
      )}

      {wizardOpen && <ClassroomWizard onClose={() => setWizardOpen(false)} />}
      </PageSection>
    </Page>
  );
};

export default ClassroomList;
